// Side-effect imports: both classes below are referenced only as types or
// through their factories, and without these the bundler drops the modules,
// the custom elements never register, and `hydrate` hands back an
// un-upgraded <std-round>.
import "@tento-ui/components/prompt";
import "@tento-ui/components/button/button";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { Divider } from "@tento-ui/components/divider";

/** `ask` — the one primitive that interrupts.
 *
 * Everything else in the family narrates; a gated effect asks. That earns
 * the family's only card, its only color while waiting, and its only
 * buttons: a transcript is read-only EXCEPT where a human is the blocker,
 * and making that the single exception is what keeps the rest of the
 * vocabulary trustworthy to skim.
 *
 * A decided ask goes gray and dim rather than disappearing, so history
 * reads as history. Option clicks surface through the caller's `onOption`
 * — the component never decides anything itself.
 *
 * ## The card and the buttons are the app's, not this file's
 *
 * Both used to be hand-rolled here: a `border-radius: 6px` div and
 * `<button class="option">` with its own padding, radius and hover. Both
 * were WRONG in the specific way a hand-rolled primitive always is — not
 * ugly on their own, but visibly not the app. The card had square-corner
 * geometry next to squircles everywhere else, and the options were 20px
 * outline chips that read as tags rather than as the one place in a
 * read-only transcript where a click does something.
 *
 * The first fix reached for `std-round` and dressed it by hand: a yellow
 * hairline and a 7%-alpha yellow wash while the ask was pending. Revised
 * on review — "remove the weird yellow tinge and border. use
 * std-prompt-layout" — and the verdict is right twice over. The tint was a
 * colour this family reserves for a WARNING, spent on a prompt that is not
 * one; and hand-dressing a round is the same mistake as hand-rolling the
 * card, one layer up. `std-prompt-layout` already IS the app's shape for
 * "something is being asked": a modal round on the elevated surface, no
 * stroke, no tint, sized and cornered like every other prompt the app
 * shows. Pending and decided now differ only in opacity and in whether the
 * buttons are live, which is what the reader actually needs to tell apart.
 *
 * `std-button` draws the options, which is also how they pick up the app's
 * focus ring, disabled treatment and press feedback for free. The variant
 * carries the semantics —
 *
 *   allow_*   primary      the affirmative default, filled
 *   reject_*  destructive  the one that stops the agent
 *   anything  secondary    "always", "edit", and whatever a harness invents
 *
 * — which matters because the option `kind` is protocol vocabulary and
 * open-ended. Anything unrecognised lands on `secondary` and still looks
 * like a button, instead of falling through to an unstyled element.
 *
 * ## The command block earns colour, and the buttons earn a rule
 *
 * A permission prompt is the one place in the transcript where the reader
 * is deciding rather than reading, so the two things that decide it get
 * help: the command's flags and operators are coloured from the app's
 * syntax palette (see `tokenizeCommand`, which is explicit about being a
 * reading aid and not a shell parser), and a rule runs edge to edge above
 * the options so the question and the decision are visibly two things.
 *
 * There is no `permission` chip any more. It was a constant string in the
 * template, identical on every instance ever rendered, so it carried no
 * information and cost the card its whole first line. What tells a reader
 * this is an interrupt is the card, the accent and the buttons — the three
 * things nothing else in the family has. */
@component({ name: "std-turn-ask" })
export class TurnAsk extends LonaWebComponent {
  private $title: HTMLElement = this.$("title");
  private $command: HTMLElement = this.$("command");
  private $options: HTMLElement = this.$("options");
  private $line: HTMLElement = this.$("line-text");

  static makeWith(props: TurnAsk.Props): TurnAsk {
    return (TurnAsk.make() as TurnAsk).bind(props);
  }

  bind(props: TurnAsk.Props): this {
    const isPending = props.state === "pending";
    this.setAttribute("state", props.state);

    // A decided ask is history, and history is one line — see the note
    // above the class. The outcome is the caller's fact, not this
    // component's: it never learns which button was pressed, because it
    // does not decide anything. Given the id it names the outcome; without
    // it the line is the question alone, which is still the useful half.
    const decided = props.options.find(
      (option) => option.optionId === props.decidedOptionId,
    );
    this.$line.textContent = decided == null
      ? props.title
      : `${props.title} · ${decided.name}`;
    this.setAttribute(
      "outcome",
      decided == null ? "unknown" : TurnAsk.outcomeFor(decided.kind),
    );

    this.$title.textContent = props.title;
    const command = props.command ?? "";
    this.$command.replaceChildren(
      ...TurnAsk.tokenizeCommand(command).map((token) => {
        const $span = document.createElement("span");
        $span.className = token.kind;
        $span.textContent = token.text;
        return $span;
      }),
    );
    this.$command.style.display = command === "" ? "none" : "";
    this.toggleAttribute("no-command", command === "");

    // Which option is THE default is a fact about the list, not about any
    // one option: "Allow" and "Allow always" are both affirmative, and
    // filling both makes two buttons compete for the one glance a
    // permission prompt gets. The first affirmative wins the fill; the
    // rest stay secondary, which is what every permission dialog worth
    // copying does.
    const primaryIndex = props.options.findIndex((option) =>
      option.kind.startsWith("allow"),
    );

    this.$options.replaceChildren(
      ...props.options.map((option, index) => {
        const $button = Button.makeWith(option.name, {
          type: TurnAsk.variantFor(option.kind, index === primaryIndex),
          onClick:
            props.onOption != null && isPending
              ? () => props.onOption!(option.optionId)
              : undefined,
        });
        // On the HOST: observedAttributes forwards both down to the inner
        // <button>. Setting them on #root directly would be undone the next
        // time the pool recycles this instance.
        $button.toggleAttribute("thin", true);
        $button.toggleAttribute("disabled", !isPending);
        return $button;
      }),
    );
    return this;
  }

  static $styles = [
    Divider.$hr,
    css`
      :host {
        display: block;
      }
      /* The layout caps itself at 360px for a centered modal; inline in a
         transcript the ask should take the column it is given. An outer-tree
         rule is the one thing that outranks a :host rule from inside, which
         is exactly what this needs. */
      #card {
        max-width: none;
      }
      #body {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px 14px;
      }
      /* ---- decided: one line, no card ------------------------------
         Revised on review: "decided should shrink to a line."

         Dimming the card to 0.7 was the wrong shape for the right idea. A
         settled permission prompt keeps the full height of a live one — the
         command block, the buttons, the whole surface — so a transcript of
         twenty answered asks is twenty cards a reader has to scroll past to
         reach the one that is still asking. The card is what says "you are
         being asked"; once you are not, it is spending the family's only
         card on a fact.

         So decided drops to the ambient register turn.note uses, in the
         same columns the tool rows use, and keeps only what a reader
         scrolling back wants: what was asked and what was answered. */
      #line {
        display: none;
        align-items: baseline;
        gap: 6px;
      }
      :host([state="decided"]) #card {
        display: none;
      }
      :host([state="decided"]) #line {
        display: flex;
      }
      /* The mark sits in the row family's own gutter, so a decided ask
         lines up with the tool rows above and below it rather than starting
         at the transcript's edge on its own. */
      #line-mark {
        flex: none;
        box-sizing: border-box;
        width: var(--tool-mark-size, 10px);
        height: var(--tool-mark-size, 10px);
        margin: 0 calc(
          (var(--turn-chevron-width, 14px) - var(--tool-mark-size, 10px)) / 2
        );
        align-self: center;
        border-radius: 50%;
        border: 1.5px solid var(--text-ter, #8b93a7);
        background: transparent;
      }
      :host([outcome="allowed"]) #line-mark {
        border-color: var(--fg-green, #2da44e);
        background: var(--fg-green, #2da44e);
      }
      :host([outcome="rejected"]) #line-mark {
        border-color: var(--fg-red, #e5484d);
        background: transparent;
      }
      #line-text {
        min-width: 0;
        --font-size: var(--small-size);
        --margin-top: 0px;
        --margin-bottom: 0px;
        color: var(--text-ter, #8b93a7);
        overflow-wrap: anywhere;
      }
      /* --font-size, NOT font-size. The app's type rule reads --font-size,
         divides it by the face's cap-height to get the real size, and
         trims the block's margins to match; a literal font-size here beats
         the size but keeps the trim computed from a DIFFERENT number, so
         the line lands off its own leading. The knob is the variable. A
         plain 12px here is also what made the question read as a caption
         on a widget rather than as something being asked. */
      /* Weight 400 and the reading size, not a heading step above it.
         Revised on review twice: "title too bold", then "title typography
         too big".

         The question was already carrying three prominence signals at
         once — a step up in size, the primary text colour, and a medium
         weight — on a card that is itself the loudest object in the
         transcript. Piling weight on top of size is the reflex, and on a
         15px line against 13px body copy the size alone is already an
         unmistakable step; the weight only made the card shout at a reader
         who had, by definition, already stopped for it. Size and colour
         keep the hierarchy — and then the size went too, because the card,
         its rule and its buttons are already saying "this one is different"
         three times over. A question set at the same size as the reply it
         interrupts is not quiet; it is simply not shouting. */
      /* Weight 400 and the reading size. Revised on review twice: "title
         too bold", then "title typography too big".

         Both were the same mistake in different clothes. The question was
         carrying three prominence signals at once — a step up in size, the
         primary text colour, and a medium weight — on a card that is
         already the loudest object in the transcript. The card, its rules
         and its buttons say "this one is different" three times over
         before the type says anything.

         --p-size, an alias, not a picked number: it is the size the reply
         it interrupts is set in, which is the right answer to "how big is
         a question" and is one fewer number in the family. */
      #title {
        min-width: 0;
        --font-size: var(--p-size);
        --margin-top: 0px;
        --margin-bottom: 0px;
        font-weight: 400;
        color: var(--text-pri, #1f2328);
        overflow-wrap: anywhere;
      }
      /* Verbatim, and monospace for the reason the exception's body is:
         a command's arguments are columns a reader scans, not prose. */
      #command {
        margin: 0;
        padding: 6px 8px;
        border-radius: 5px;
        background: var(--fill-sec, rgba(127, 127, 127, 0.08));
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        --font-size: var(--turn-fine-size, var(--text-8px));
        --line-height-ratio: 1.5;
        color: var(--text-sec);
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 128px;
        overflow: hidden auto;
      }
      /* The app's syntax palette, not four picked hues. Revised on review:
         "content needs syntax coloring."

         Literal fallbacks on every one, because the surface where this is
         reviewed is a sandboxed card that may resolve no theme at all —
         and an unresolved var() here collapses the distinction this whole
         change exists to draw. */
      #command .command {
        color: var(--fg-syntax-function, #6f42c1);
      }
      #command .flag {
        color: var(--fg-syntax-keyword, #cf222e);
      }
      #command .operator {
        color: var(--fg-syntax-operator, #0550ae);
      }
      /* Two rules, both edge to edge against the card's own inset rather
         than the body's: a rule that stops 14px short of the corner reads
         as a line drawn IN the content, and these are separating the card's
         three parts. Revised on review: "divider above the buttons", then
         "add a divider below the title".

         Which is the card's structure made visible — it asks, it shows, it
         offers, and each is a different kind of thing. The title rule goes
         with the command block, so a question with nothing to show keeps
         one rule rather than two stacked with nothing between them. */
      #rule-title,
      #rule {
        margin-block: 2px 0;
        margin-inline: -14px;
        width: auto;
      }
      :host([no-command]) #rule-title {
        display: none;
      }
      #options {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-prompt-layout id="card">
      <div id="body" slot="text">
        <p id="title"></p>
        <hr id="rule-title" />
        <pre class="code-block" id="command"></pre>
        <hr id="rule" />
        <div id="options"></div>
      </div>
    </std-prompt-layout>
    <div id="line">
      <span id="line-mark"></span>
      <p id="line-text"></p>
    </div>
  `;
}

export namespace TurnAsk {
  export type State = "pending" | "decided";

  /** What a word in the command block is, for colour purposes only. */
  export type TokenKind = "command" | "flag" | "operator" | "plain";

  export type Token = { text: string; kind: TokenKind };

  /** The words a shell reads as punctuation between commands. */
  const OPERATORS: ReadonlySet<string> = new Set([
    "|", "||", "&&", ";", "&", ">", ">>", "<", "<<", "2>&1", "|&",
  ]);

  /** Colour for a shell command line — a READING AID, not a parser, and
   * the distinction is the whole design.
   *
   * The reflex was `std-code`, and it is the wrong tool twice. It resolves
   * anything it does not know to `Plain`, and it knows lisp, js, ts and
   * rust — so a shell command gets no colour from it at all. It also loads
   * its highlighter from the static host at runtime, which 404s on the
   * review node (measured), so the one surface where this card is looked
   * at would have shown an empty frame. Nothing else in the repo colours
   * shell. There is no primitive being reimplemented here.
   *
   * What a permission prompt is actually for is deciding, in one glance,
   * whether to let a command run — and the thing that decides it is almost
   * never the program. It is the FLAGS (`-rf`, `--force`, `--no-verify`)
   * and the OPERATORS, because those are where an innocuous-looking line
   * turns destructive. So those are what get colour, and the argument
   * words stay quiet.
   *
   * Its boundary, stated rather than discovered later: it does not parse
   * quoting. A word inside a quoted payload is coloured as though it stood
   * on the command line, so the `--short` in
   * `zsh -lc "git status --short"` reads as the flag it is to a human,
   * which is not what the shell does with it. That is the useful lie, and
   * it is only ever a lie about COLOUR — the text is untouched and the
   * block stays verbatim. A caller that needs true shell semantics needs a
   * shell parser, and should not get it from a prompt card. */
  export function tokenizeCommand(command: string): Token[] {
    const tokens: Token[] = [];
    // Whitespace is captured, not dropped: the block is verbatim, and a
    // command block that reflowed its own spacing would be lying about
    // what is being approved.
    let atCommandStart = true;
    for (const part of command.split(/(\s+)/)) {
      if (part === "") continue;
      if (/^\s+$/.test(part)) {
        // A newline starts a new line, and a new line starts a command.
        if (part.includes("\n")) atCommandStart = true;
        tokens.push({ text: part, kind: "plain" });
        continue;
      }
      let kind: TokenKind = "plain";
      if (OPERATORS.has(part)) {
        kind = "operator";
        atCommandStart = true;
      } else if (part.replace(/^["'`]+/, "").startsWith("-")) {
        kind = "flag";
      } else if (atCommandStart) {
        kind = "command";
        atCommandStart = false;
      }
      tokens.push({ text: part, kind });
    }
    return tokens;
  }

  export type Variant = "primary" | "secondary" | "destructive";

  /** Protocol option kinds are open-ended, so this matches a PREFIX and
   * falls through to `secondary` rather than switching on a closed set. A
   * harness that invents `allow_for_session` gets the affirmative
   * treatment without this file being edited; one that invents something
   * unrecognised still gets a real button.
   *
   * `isDefault` is the caller's answer to "is this the ONE affirmative",
   * which the kind alone cannot say — see the comment at the call site. */
  export function variantFor(kind: string, isDefault: boolean): Variant {
    if (kind.startsWith("allow")) return isDefault ? "primary" : "secondary";
    if (kind.startsWith("reject") || kind.startsWith("deny")) {
      return "destructive";
    }
    return "secondary";
  }

  /** What a decided ask ANSWERED, for the one-line history face. Matches a
   * prefix like `variantFor` and for the same reason: option kinds are open
   * protocol vocabulary, so an unrecognised one gets a neutral mark rather
   * than falling through to nothing. */
  export function outcomeFor(kind: string): "allowed" | "rejected" | "other" {
    if (kind.startsWith("allow")) return "allowed";
    if (kind.startsWith("reject") || kind.startsWith("deny")) return "rejected";
    return "other";
  }

  export type Option = {
    optionId: string;
    name: string;
    /** Protocol option kind — "allow_once", "allow_always", "reject_once", … */
    kind: string;
  };

  export type Props = {
    title: string;
    command?: string;
    options: Option[];
    state: State;
    /** Which option was taken, once `state` is "decided". Optional: a
     * caller that did not keep the answer still gets the collapsed line,
     * with the question on it and a neutral mark. */
    decidedOptionId?: string;
    onOption?: (optionId: string) => void;
  };
}
